import React, { useState, useEffect, useMemo } from 'react'
import VoucherCard from '../../../../gift-cards-v2/VoucherCard'
import { isHtmlBlock, renderBlockHtml, themeForBlocks } from '../email/render'
import { ShadowHtml } from './EmailHtml'
import { getTheme } from '../../../../gift-cards-v2/data'
// VoucherCard's .v2-card styles are scoped under .voucher-v2 (same pattern as
// the public gift card shop's GiftCardPreview)
import '../../../../gift-cards-v2/GiftCardsV2.css'

// Builds a scalloped circle path (flower / scallop crops). Points sit on a
// circle centred at (50,50); each pair is joined by an outward arc whose
// radius controls how deep the petals read. The base radius is derived so the
// petal bulges land exactly on the 100-box edge instead of overshooting it
// and getting flat-cut by the viewBox.
const scallopPath = (lobes, depth) => {
  const k = 2 * Math.sin(Math.PI / lobes) // chord / base radius
  const arcOverChord = Math.max(depth, 0.5) // arc radius as a fraction of the chord
  // Sagitta (bulge height) per unit of base radius
  const kd = k * arcOverChord
  const sag = kd - Math.sqrt(Math.max(0, kd * kd - (k * k) / 4))
  const R = 50 / (1 + sag)

  const pts = []
  for (let i = 0; i < lobes; i++) {
    const a = (Math.PI * 2 * i) / lobes - Math.PI / 2
    pts.push([50 + R * Math.cos(a), 50 + R * Math.sin(a)])
  }
  const r = (2 * R * Math.sin(Math.PI / lobes) * arcOverChord).toFixed(2)
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`
  for (let i = 1; i <= lobes; i++) {
    const [x, y] = pts[i % lobes]
    d += ` A ${r} ${r} 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)}`
  }
  return d + ' Z'
}

// Crop shapes for the shape-image block, all in a 100-wide box
const SHAPE_DEFS = {
  arch: { viewH: 130, d: 'M 0 50 A 50 50 0 0 1 100 50 L 100 130 L 0 130 Z' },
  circle: { viewH: 100, d: 'M 50 0 A 50 50 0 1 1 49.99 0 Z' },
  flower: { viewH: 100, d: scallopPath(8, 0.5) },
  scallop: { viewH: 100, d: scallopPath(12, 0.55) },
  heart: { viewH: 100, d: 'M 50 91 C 18 66 4 48 4 30 C 4 14 17 4 30 4 C 39 4 46 9 50 16 C 54 9 61 4 70 4 C 83 4 96 14 96 30 C 96 48 82 66 50 91 Z' },
  blob: { viewH: 100, d: 'M 84 20 C 95 33 97 56 88 72 C 79 88 58 97 40 93 C 22 89 8 74 4 56 C 0 38 9 18 25 8 C 41 -2 73 7 84 20 Z' }
}

// Self-contained countdown block that calculates live remaining time from endDate
const CountdownBlock = ({ content, isMobile, wrapperStyle, handleClick }) => {
  const [timeLeft, setTimeLeft] = useState(null)

  useEffect(() => {
    if (!content.endDate) return
    const end = new Date(content.endDate)
    if (isNaN(end.getTime())) return

    const calculate = () => {
      const diff = Math.max(0, end - new Date())
      setTimeLeft({
        days: String(Math.floor(diff / (1000 * 60 * 60 * 24))).padStart(2, '0'),
        hours: String(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).padStart(2, '0'),
        mins: String(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0'),
        secs: String(Math.floor((diff % (1000 * 60)) / 1000)).padStart(2, '0')
      })
    }

    calculate()
    const interval = setInterval(calculate, 1000)
    return () => clearInterval(interval)
  }, [content.endDate])

  const d = timeLeft?.days ?? content.days ?? '00'
  const h = timeLeft?.hours ?? content.hours ?? '00'
  const m = timeLeft?.mins ?? content.mins ?? '00'
  const s = timeLeft?.secs ?? content.secs ?? '00'

  const digitBoxStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }
  const digitStyle = {
    backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: '12px',
    width: isMobile ? '56px' : '72px', height: isMobile ? '64px' : '80px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: isMobile ? '28px' : '36px', fontWeight: '800',
    color: content.textColor || '#ffffff',
    fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
    letterSpacing: '-0.02em'
  }
  const labelStyle = {
    fontSize: '10px', fontWeight: '600', color: content.textColor || '#ffffff',
    opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.08em'
  }
  const colonStyle = {
    fontSize: isMobile ? '24px' : '32px', fontWeight: '700',
    color: content.textColor || '#ffffff', opacity: 0.4,
    alignSelf: 'flex-start', paddingTop: isMobile ? '14px' : '18px'
  }

  return (
    <div
      onClick={handleClick}
      style={{
        ...wrapperStyle,
        fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
        backgroundColor: content.backgroundColor || '#1a1a1a',
        padding: isMobile ? '40px 20px' : '56px 48px',
        textAlign: 'center'
      }}
    >
      <h3 style={{
        fontSize: isMobile ? '18px' : '22px', fontWeight: '600',
        color: content.textColor || '#ffffff',
        margin: '0 0 24px 0', letterSpacing: '0.02em', opacity: 0.9
      }}>
        {content.headline || 'Offer Ends In'}
      </h3>
      <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? '8px' : '12px', alignItems: 'flex-start' }}>
        <div style={digitBoxStyle}><div style={digitStyle}>{d}</div><span style={labelStyle}>Days</span></div>
        <span style={colonStyle}>:</span>
        <div style={digitBoxStyle}><div style={digitStyle}>{h}</div><span style={labelStyle}>Hours</span></div>
        <span style={colonStyle}>:</span>
        <div style={digitBoxStyle}><div style={digitStyle}>{m}</div><span style={labelStyle}>Mins</span></div>
        <span style={colonStyle}>:</span>
        <div style={digitBoxStyle}><div style={digitStyle}>{s}</div><span style={labelStyle}>Secs</span></div>
      </div>
    </div>
  )
}

// Renders the visual preview of email blocks
// Email-native sections (and every block of a themed email) show the exact
// HTML that gets sent, isolated in a shadow root from the app's CSS.
const FALLBACK_THEME = themeForBlocks([])
const HtmlBlock = ({ block, theme, isEditable, onSelect }) => {
  const html = useMemo(() => renderBlockHtml(block, theme || FALLBACK_THEME, { builder: isEditable }), [block, theme, isEditable])
  const handleClick = (e) => {
    if (isEditable && onSelect) {
      e.stopPropagation()
      onSelect(block.id)
    }
  }
  return <ShadowHtml html={html} onClick={handleClick} tags={isEditable} style={isEditable ? { cursor: 'pointer' } : undefined} />
}

export const BlockRenderer = ({ block, isEditable = false, onSelect, isSelected = false, previewMode = 'desktop', emailTheme }) => {
  if (emailTheme || isHtmlBlock(block)) {
    return <HtmlBlock block={block} theme={emailTheme} isEditable={isEditable} onSelect={onSelect} />
  }
  const isMobile = previewMode === 'mobile'
  const content = block.content || {}

  // Selection highlight is drawn by the canvas block wrapper (.is-selected),
  // which can round its corners to match the email card's radius — a square
  // outline here got visibly cropped on the first and last blocks.
  const wrapperStyle = isEditable ? {
    position: 'relative',
    cursor: 'pointer'
  } : {}

  const handleClick = (e) => {
    // Never follow links inside the builder or previews. Hrefs hold merge
    // tags like {{BookingLink}} that only resolve at send time, so navigating
    // lands on a literal /%7B%7BBookingLink%7D%7D 404.
    if (e.target && e.target.closest && e.target.closest('a')) {
      e.preventDefault()
    }
    if (isEditable && onSelect) {
      e.stopPropagation()
      onSelect(block.id)
    }
  }

  // Text Block
  if (block.type === 'text') {
    const lines = (content.text || '').split('\n')
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '20px 32px',
          backgroundColor: content.backgroundColor || 'transparent',
          textAlign: content.align || 'left'
        }}
      >
        {lines.map((line, idx) => (
          <p
            key={idx}
            style={{
              fontSize: `${content.fontSize || 16}px`,
              color: content.textColor || '#374151',
              fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
              fontWeight: content.bold ? '700' : '400',
              lineHeight: content.lineHeight || '1.6',
              letterSpacing: content.letterSpacing || 'normal',
              textTransform: content.uppercase ? 'uppercase' : 'none',
              margin: idx === lines.length - 1 ? 0 : '0 0 8px 0'
            }}
          >
            {line || '\u00A0'}
          </p>
        ))}
      </div>
    )
  }

  // Logo Block
  if (block.type === 'logo') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: '20px 32px',
          textAlign: content.align || 'center',
          backgroundColor: content.backgroundColor || 'transparent'
        }}
      >
        {content.url ? (
          <img
            src={content.url}
            alt="Logo"
            style={{ maxWidth: `${content.maxWidth || 150}px`, height: 'auto' }}
          />
        ) : (
          <div style={{
            display: 'inline-block',
            padding: '20px 40px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            + Add Logo
          </div>
        )}
      </div>
    )
  }

  // Image Block
  if (block.type === 'image') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: content.fullWidth ? 0 : '20px 32px',
          backgroundColor: content.backgroundColor || 'transparent'
        }}
      >
        {content.url ? (
          <img
            src={content.url}
            alt={content.alt || ''}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: content.fullWidth ? 0 : '8px'
            }}
          />
        ) : (
          <div style={{
            padding: '60px 20px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            + Add Image
          </div>
        )}
      </div>
    )
  }

  // Button Block
  if (block.type === 'button') {
    const isOutline = content.variant === 'outline'
    const isPill = content.shape === 'pill'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '20px 32px',
          textAlign: content.align || 'center',
          backgroundColor: content.backgroundColor || 'transparent'
        }}
      >
        <a
          href={content.url || '#'}
          style={{
            display: 'inline-block',
            padding: isPill ? '13px 36px' : '14px 28px',
            backgroundColor: isOutline ? 'transparent' : (content.buttonColor || '#29282D'),
            color: isOutline ? (content.buttonColor || '#29282D') : (content.textColor || '#ffffff'),
            border: isOutline ? `1.5px solid ${content.buttonColor || '#29282D'}` : 'none',
            borderRadius: isPill ? '100px' : '8px',
            fontSize: `${content.fontSize || 14}px`,
            fontWeight: '600',
            letterSpacing: content.uppercase ? '0.12em' : (content.letterSpacing || 'normal'),
            textTransform: content.uppercase ? 'uppercase' : 'none',
            textDecoration: 'none',
            fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif'
          }}
        >
          {content.text || 'Button'}
        </a>
      </div>
    )
  }

  // Divider Block
  if (block.type === 'divider') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: '20px 32px'
        }}
      >
        <hr style={{
          border: 'none',
          borderTop: `1px solid ${content.color || '#e5e5e5'}`,
          margin: 0
        }} />
      </div>
    )
  }

  // Spacer Block
  if (block.type === 'spacer') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          height: `${content.height || 30}px`
        }}
      />
    )
  }

  // Social Block
  if (block.type === 'social') {
    const socialLinks = [
      { id: 'facebook', url: content.facebook, icon: 'f' },
      { id: 'instagram', url: content.instagram, icon: 'i' },
      { id: 'twitter', url: content.twitter, icon: 'x' },
      { id: 'tiktok', url: content.tiktok, icon: 't' }
    ].filter(s => s.url)

    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: '20px 32px',
          textAlign: 'center'
        }}
      >
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          {socialLinks.length > 0 ? socialLinks.map(social => (
            <a
              key={social.id}
              href={social.url}
              style={{
                width: '36px',
                height: '36px',
                backgroundColor: content.iconColor || '#29282D',
                borderRadius: content.style === 'circle' ? '50%' : '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none'
              }}
            >
              {social.icon}
            </a>
          )) : (
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>Add social links</span>
          )}
        </div>
      </div>
    )
  }

  // Columns Block
  if (block.type === 'columns') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: '20px 32px'
        }}
      >
        <div style={{ display: 'flex', gap: `${content.gap || 20}px` }}>
          {/* Left Column */}
          <div style={{ flex: 1 }}>
            {(content.leftContent || []).length > 0 ? (
              content.leftContent.map((item, idx) => (
                <ColumnItem key={item.id || idx} item={item} />
              ))
            ) : (
              <div style={{ padding: '40px 20px', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                Left column
              </div>
            )}
          </div>
          {/* Right Column */}
          <div style={{ flex: 1 }}>
            {(content.rightContent || []).length > 0 ? (
              content.rightContent.map((item, idx) => (
                <ColumnItem key={item.id || idx} item={item} />
              ))
            ) : (
              <div style={{ padding: '40px 20px', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                Right column
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Footer Block — always carries the UK-compliance lines: sender identity
  // (PECR reg 23 / Companies Act trading disclosures) and an unsubscribe
  // link (PECR reg 22 / UK GDPR right to object). They render on every
  // email and cannot be removed; merge tags resolve at send time.
  if (block.type === 'footer') {
    const inkColor = content.textColor || '#9ca3af'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '32px',
          backgroundColor: content.backgroundColor || '#f9fafb',
          textAlign: 'center'
        }}
      >
        {content.text && (
          <p style={{ fontSize: '12px', color: inkColor, lineHeight: '1.6', margin: 0, whiteSpace: 'pre-line' }}>
            {content.text}
          </p>
        )}
        <div style={{ marginTop: content.text ? '14px' : 0, paddingTop: content.text ? '14px' : 0, borderTop: content.text ? '1px solid rgba(128,128,128,0.18)' : 'none' }}>
          <p style={{ fontSize: '12px', color: inkColor, lineHeight: '1.6', margin: '0 0 6px 0' }}>
            {'{{SalonName}}'} · {'{{SalonAddress}}'}
          </p>
          <p style={{ fontSize: '11px', color: inkColor, opacity: 0.8, lineHeight: '1.6', margin: '0 0 6px 0' }}>
            {'{{CompanyDetails}}'}
          </p>
          <p style={{ fontSize: '12px', color: inkColor, lineHeight: '1.6', margin: 0 }}>
            <span style={{ textDecoration: 'underline' }}>Unsubscribe</span>
            {' · '}
            <span style={{ textDecoration: 'underline' }}>Update preferences</span>
          </p>
        </div>
      </div>
    )
  }

  // Review Block
  if (block.type === 'review') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '24px 32px',
          backgroundColor: content.sectionColor || 'transparent'
        }}
      >
        <div style={{
          padding: '28px 32px',
          backgroundColor: content.backgroundColor || '#faf9f7',
          borderRadius: '12px',
          textAlign: 'center',
          position: 'relative'
        }}>
          {/* Decorative quote */}
          <div style={{
            fontSize: '48px', lineHeight: 1, color: '#e5e7eb', fontFamily: 'Georgia, serif',
            marginBottom: '8px', userSelect: 'none'
          }}>"</div>
          {/* Stars */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '3px', marginBottom: '16px' }}>
            {[1,2,3,4,5].map(i => (
              <svg key={i} width="18" height="18" viewBox="0 0 20 20" fill={i <= (content.rating || 5) ? '#f59e0b' : '#e5e7eb'}>
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <p style={{
            fontSize: '16px', color: content.textColor || '#374151', lineHeight: '1.7',
            margin: '0 auto 20px', fontStyle: 'italic', maxWidth: '420px'
          }}>
            "{content.text || 'Customer review text...'}"
          </p>
          <div>
            <p style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 2px 0', letterSpacing: '-0.01em' }}>
              {content.author || 'Client Name'}
            </p>
            {content.service && (
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{content.service}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Service Block — variants: card (image left), stacked (image top), minimal (menu row)
  if (block.type === 'service') {
    const variant = content.variant || 'card'
    const currency = content.currency || '£'

    if (variant === 'minimal') {
      return (
        <div
          onClick={handleClick}
          style={{
            ...wrapperStyle,
            fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
            padding: '4px 32px',
            backgroundColor: content.sectionColor || 'transparent'
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: '16px',
            padding: '18px 4px', borderBottom: '1px solid rgba(0,0,0,0.08)'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{
                fontSize: '16px', fontWeight: '600', color: content.textColor || '#1a1a1a',
                margin: '0 0 3px 0', letterSpacing: '-0.01em'
              }}>
                {content.name || 'Service Name'}
              </h4>
              {content.description && (
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5', margin: 0 }}>
                  {content.description}
                </p>
              )}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ fontSize: '17px', fontWeight: '700', color: content.priceColor || '#1a1a1a', display: 'block' }}>
                {currency}{content.price || '0'}
              </span>
              {content.duration && (
                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>{content.duration}</span>
              )}
            </div>
          </div>
        </div>
      )
    }

    const stacked = variant === 'stacked'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '24px 32px',
          backgroundColor: content.sectionColor || 'transparent'
        }}
      >
        <div style={{
          display: 'flex', flexDirection: (stacked || isMobile) ? 'column' : 'row', gap: (stacked || isMobile) ? '0' : '24px', alignItems: 'stretch',
          backgroundColor: content.backgroundColor || '#fafafa', borderRadius: '12px', overflow: 'hidden'
        }}>
          {/* Image (omitted entirely when the service has none) */}
          {content.image && (
            <div style={{
              width: (stacked || isMobile) ? '100%' : '160px',
              minHeight: stacked ? '200px' : (isMobile ? '180px' : '160px'),
              flexShrink: 0,
              backgroundImage: `url(${content.image})`,
              backgroundColor: '#e5e7eb',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
          )}
          {/* Info */}
          <div style={{ flex: 1, padding: content.image ? ((stacked || isMobile) ? '20px 24px' : '20px 24px 20px 0') : '22px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h4 style={{
              fontSize: '18px', fontWeight: '700', color: content.textColor || '#1a1a1a',
              margin: '0 0 6px 0', letterSpacing: '-0.01em'
            }}>
              {content.name || 'Service Name'}
            </h4>
            <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5', margin: '0 0 16px 0' }}>
              {content.description || 'Service description...'}
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '22px', fontWeight: '800', color: content.priceColor || '#007EA0' }}>
                {currency}{content.price || '0'}
              </span>
              {content.duration && (
                <span style={{
                  fontSize: '12px', color: '#9ca3af', fontWeight: '500',
                  padding: '2px 8px', backgroundColor: '#f3f4f6', borderRadius: '4px'
                }}>
                  {content.duration}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Product Block — retail product with a deep link to the salon's online shop.
  // Variants: card (image left), stacked (image top), minimal (menu row).
  if (block.type === 'product') {
    const currency = content.currency || '£'
    const variant = content.variant || 'card'

    if (variant === 'minimal') {
      return (
        <div
          onClick={handleClick}
          style={{
            ...wrapperStyle,
            fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
            padding: '4px 32px',
            backgroundColor: content.sectionColor || 'transparent'
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '18px 4px', borderBottom: '1px solid rgba(0,0,0,0.08)'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{
                fontSize: '16px', fontWeight: '600', color: content.textColor || '#1a1a1a',
                margin: '0 0 3px 0', letterSpacing: '-0.01em'
              }}>
                {content.name || 'Product Name'}
              </h4>
              <span style={{ fontSize: '15px', fontWeight: '700', color: content.priceColor || '#1a1a1a' }}>
                {currency}{content.price || '0'}
              </span>
            </div>
            <a
              href={content.url || '#'}
              style={{
                display: 'inline-block', flexShrink: 0,
                padding: '9px 18px',
                backgroundColor: content.buttonColor || '#29282D',
                color: content.buttonTextColor || '#ffffff',
                borderRadius: '100px',
                fontSize: '11px', fontWeight: '600',
                letterSpacing: '0.08em', textTransform: 'uppercase',
                textDecoration: 'none',
                fontFamily: 'Inter, system-ui, sans-serif'
              }}
            >
              {content.buttonText || 'Shop now'}
            </a>
          </div>
        </div>
      )
    }

    const stacked = variant === 'stacked'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '24px 32px',
          backgroundColor: content.sectionColor || 'transparent'
        }}
      >
        <div style={{
          display: 'flex', flexDirection: (stacked || isMobile) ? 'column' : 'row', gap: (stacked || isMobile) ? '0' : '24px', alignItems: 'stretch',
          backgroundColor: content.backgroundColor || '#fafafa', borderRadius: '12px', overflow: 'hidden'
        }}>
          {content.image && (
            <div style={{
              width: (stacked || isMobile) ? '100%' : '170px',
              minHeight: stacked ? '220px' : (isMobile ? '200px' : '170px'),
              flexShrink: 0,
              backgroundImage: `url(${content.image})`,
              backgroundColor: '#e5e7eb',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }} />
          )}
          <div style={{ flex: 1, padding: content.image ? ((stacked || isMobile) ? '20px 24px' : '22px 24px 22px 0') : '22px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h4 style={{
              fontSize: '18px', fontWeight: '700', color: content.textColor || '#1a1a1a',
              margin: '0 0 6px 0', letterSpacing: '-0.01em'
            }}>
              {content.name || 'Product Name'}
            </h4>
            {content.description && (
              <p style={{
                fontSize: '14px', color: '#6b7280', lineHeight: '1.5', margin: '0 0 16px 0',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
              }}>
                {content.description}
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '21px', fontWeight: '800', color: content.priceColor || '#1a1a1a', letterSpacing: '-0.02em' }}>
                {currency}{content.price || '0'}
              </span>
              <a
                href={content.url || '#'}
                style={{
                  display: 'inline-block',
                  padding: '10px 22px',
                  backgroundColor: content.buttonColor || '#29282D',
                  color: content.buttonTextColor || '#ffffff',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: '600',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}
              >
                {content.buttonText || 'Shop now'}
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Voucher Block — renders the salon's REAL voucher design via the
  // gift-card designer's VoucherCard, with a buy button deep-linking to the
  // public gift card shop. Variants: showcase (big card), split (card beside
  // details), minimal (banner row, no card art).
  if (block.type === 'voucher') {
    const currency = content.currency || '£'
    const variant = content.variant || 'showcase'
    const theme = getTheme(content.theme || 'rose')
    const previewVoucher = {
      id: 'email-preview',
      name: content.name || 'Gift Voucher',
      template: content.template || 'signature',
      theme: content.theme || 'rose',
      value: Number(content.value) || Number(content.fromPrice) || 50,
      promo: (content.promo !== undefined && content.promo !== null && content.promo !== '') ? Number(content.promo) : null,
      denominations: [],
      status: 'active',
      // Salon-built custom designer config (drives CustomCardScene)
      ...(content.customDesign ? { customDesign: content.customDesign } : {})
    }
    const buttonBg = content.buttonColor || theme.accent
    const buttonInk = content.buttonTextColor || (theme.ink === 'dark' ? '#FFFFFF' : theme.bg)
    const fromLine = (
      <>
        From {currency}{content.fromPrice || previewVoucher.value}
        {content.expiry && content.expiry !== 'Never' && (
          <span style={{ fontWeight: '400', color: '#6b7280' }}> · valid for {content.expiry}</span>
        )}
      </>
    )
    const buyButton = (extraStyle = {}) => (
      <a
        href={content.url || '#'}
        style={{
          display: 'inline-block',
          padding: '12px 30px',
          backgroundColor: buttonBg,
          color: buttonInk,
          borderRadius: '100px',
          fontSize: '12px',
          fontWeight: '600',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          textDecoration: 'none',
          fontFamily: 'Inter, system-ui, sans-serif',
          ...extraStyle
        }}
      >
        {content.buttonText || 'Buy gift voucher'}
      </a>
    )

    if (variant === 'minimal') {
      return (
        <div
          onClick={handleClick}
          style={{
            ...wrapperStyle,
            fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
            padding: '4px 32px',
            backgroundColor: content.sectionColor || 'transparent'
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '18px 4px', borderBottom: '1px solid rgba(0,0,0,0.08)'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{
                fontSize: '16px', fontWeight: '600', color: content.textColor || '#1a1a1a',
                margin: '0 0 3px 0', letterSpacing: '-0.01em'
              }}>
                {content.name || 'Gift Voucher'}
              </h4>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>{fromLine}</span>
            </div>
            {buyButton({ padding: '9px 18px', fontSize: '11px', flexShrink: 0 })}
          </div>
        </div>
      )
    }

    if (variant === 'split') {
      return (
        <div
          onClick={handleClick}
          style={{
            ...wrapperStyle,
            fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
            padding: isMobile ? '24px' : '28px 48px',
            backgroundColor: content.sectionColor || 'transparent'
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '20px' : '28px',
            alignItems: 'center'
          }}>
            <div className="voucher-v2 eb-card-frame" style={{ width: isMobile ? '100%' : '52%', flexShrink: 0 }}>
              <VoucherCard
                voucher={previewVoucher}
                aesthetic={content.aesthetic || 'editorial'}
                salonName={content.salonName || 'Your Salon'}
                currency={currency}
                showPromo={true}
              />
            </div>
            <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
              <h4 style={{
                fontSize: '19px', fontWeight: '700', color: content.textColor || '#1a1a1a',
                margin: '0 0 6px 0', letterSpacing: '-0.01em'
              }}>
                {content.name || 'Gift Voucher'}
              </h4>
              <p style={{ fontSize: '13.5px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 16px 0' }}>
                {fromLine}
              </p>
              {buyButton()}
            </div>
          </div>
        </div>
      )
    }

    // showcase (default)
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '24px' : '28px 48px',
          backgroundColor: content.sectionColor || 'transparent',
          textAlign: 'center'
        }}
      >
        <div className="voucher-v2 eb-card-frame" style={{ maxWidth: '420px', margin: '0 auto', textAlign: 'left' }}>
          <VoucherCard
            voucher={previewVoucher}
            aesthetic={content.aesthetic || 'editorial'}
            salonName={content.salonName || 'Your Salon'}
            currency={currency}
            showPromo={true}
          />
        </div>
        <p style={{
          fontSize: '13.5px',
          fontWeight: '600',
          color: '#1a1a1a',
          margin: '18px 0 16px 0',
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          {fromLine}
        </p>
        {buyButton()}
      </div>
    )
  }

  // Team Block
  if (block.type === 'team') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: '32px',
          textAlign: 'center',
          backgroundColor: content.backgroundColor || 'transparent'
        }}
      >
        {/* Avatar with ring */}
        <div style={{
          width: '130px', height: '130px', borderRadius: '50%',
          margin: '0 auto 20px',
          padding: '4px',
          background: content.ringColor || 'linear-gradient(135deg, #007EA0 0%, #059669 100%)'
        }}>
          {content.image ? (
            <img
              src={content.image}
              alt={content.name || 'Team member'}
              style={{
                width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover',
                border: '3px solid white'
              }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              backgroundColor: '#f3f4f6', border: '3px solid white',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c4c4c4" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
          )}
        </div>
        <h4 style={{
          fontSize: '20px', fontWeight: '700', color: content.textColor || '#1a1a1a',
          margin: '0 0 4px 0', letterSpacing: '-0.01em'
        }}>
          {content.name || 'Team Member'}
        </h4>
        <p style={{
          fontSize: '14px', color: content.accentColor || '#007EA0', fontWeight: '600', margin: '0 0 14px 0',
          letterSpacing: '0.02em'
        }}>
          {content.title || 'Position'}
        </p>
        {content.bio && (
          <p style={{
            fontSize: '14px', color: '#6b7280', lineHeight: '1.7', margin: 0,
            maxWidth: '380px', marginLeft: 'auto', marginRight: 'auto'
          }}>
            {content.bio}
          </p>
        )}
      </div>
    )
  }

  // ==========================================
  // LAYOUT BLOCKS — Bold, Modern, Responsive
  // ==========================================

  // Layout: Gradient Hero
  if (block.type === 'layout-gradient') {
    const gradients = {
      purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      teal: 'linear-gradient(135deg, #007EA0 0%, #059669 100%)',
      sunset: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      dark: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      warm: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
    }
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          background: gradients[content.gradient] || gradients.purple,
          padding: isMobile ? '56px 24px' : '80px 48px',
          textAlign: 'center'
        }}
      >
        <h1 style={{
          fontSize: isMobile ? '32px' : '44px',
          fontWeight: '800',
          color: content.textColor || 'white',
          margin: '0 0 16px 0',
          letterSpacing: '-0.02em',
          lineHeight: '1.1',
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif'
        }}>
          {content.headline || 'HEADLINE'}
        </h1>
        {content.subtext && (
          <p style={{
            fontSize: isMobile ? '15px' : '18px',
            color: content.textColor || 'white',
            opacity: 0.85,
            margin: 0,
            maxWidth: '480px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.6'
          }}>
            {content.subtext}
          </p>
        )}
      </div>
    )
  }

  // Layout: Arch Hero
  if (block.type === 'layout-arch') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          position: 'relative',
          height: isMobile ? '280px' : '360px',
          overflow: 'hidden'
        }}
      >
        {content.imageUrl ? (
          <img
            src={content.imageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', backgroundColor: '#d4d4d8' }} />
        )}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: isMobile ? '32px 24px 48px' : '48px 48px 56px',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '800',
            color: content.textColor || 'white',
            margin: '0 0 8px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.1'
          }}>
            {content.headline || 'Welcome'}
          </h1>
          {content.subtext && (
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: content.textColor || 'white',
              opacity: 0.9,
              margin: 0,
              maxWidth: '400px'
            }}>
              {content.subtext}
            </p>
          )}
        </div>
        {/* Arch mask */}
        <div style={{
          position: 'absolute',
          bottom: '-50%',
          left: '-10%',
          right: '-10%',
          height: '60%',
          backgroundColor: 'white',
          borderRadius: '100% 100% 0 0'
        }} />
      </div>
    )
  }

  // Layout: Full Bleed Hero (NEW)
  if (block.type === 'layout-full-bleed') {
    const overlayOpacity = (content.overlayOpacity != null ? content.overlayOpacity : 40) / 100
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          position: 'relative',
          minHeight: isMobile ? '300px' : '400px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {content.imageUrl ? (
          <img
            src={content.imageUrl}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: content.backgroundColor || '#1a1a1a' }} />
        )}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: `rgba(0,0,0,${overlayOpacity})`
        }} />
        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          padding: isMobile ? '40px 24px' : '60px 48px',
          maxWidth: '600px'
        }}>
          <h1 style={{
            fontSize: isMobile ? '32px' : '44px',
            fontWeight: '800',
            color: content.textColor || 'white',
            margin: '0 0 16px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.1'
          }}>
            {content.headline || 'Your Beauty Journey Starts Here'}
          </h1>
          {content.subtext && (
            <p style={{
              fontSize: isMobile ? '15px' : '18px',
              color: content.textColor || 'white',
              opacity: 0.85,
              margin: 0,
              lineHeight: '1.6'
            }}>
              {content.subtext}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Layout: Hero Split (NEW)
  if (block.type === 'layout-hero-split') {
    const imageLeft = (content.imagePosition || 'left') === 'left'
    const imageBlock = (
      <div style={{
        flex: 1,
        minHeight: isMobile ? '240px' : '360px',
        backgroundColor: '#e5e7eb',
        backgroundImage: content.imageUrl ? `url(${content.imageUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        {!content.imageUrl && (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isMobile ? '240px' : '360px' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c4c4c4" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
        )}
      </div>
    )
    const contentBlock = (
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: isMobile ? '36px 24px' : '48px',
        backgroundColor: content.backgroundColor || '#ffffff'
      }}>
        <h2 style={{
          fontSize: isMobile ? '28px' : '34px',
          fontWeight: '700',
          color: content.textColor || '#1a1a1a',
          margin: '0 0 16px 0',
          letterSpacing: '-0.02em',
          lineHeight: '1.15'
        }}>
          {content.headline || 'Meet Your Stylist'}
        </h2>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: '#6b7280',
          lineHeight: '1.7',
          margin: 0
        }}>
          {content.body || 'Passionate about helping you look and feel your best.'}
        </p>
      </div>
    )
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden'
        }}
      >
        {isMobile ? (
          <>{imageBlock}{contentBlock}</>
        ) : imageLeft ? (
          <>{imageBlock}{contentBlock}</>
        ) : (
          <>{contentBlock}{imageBlock}</>
        )}
      </div>
    )
  }

  // Layout: Editorial (image + text side by side)
  if (block.type === 'layout-editorial') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '32px 24px' : '48px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '24px' : '40px',
          alignItems: 'center',
          backgroundColor: content.backgroundColor || 'transparent'
        }}
      >
        <div style={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
          {content.imageUrl ? (
            <img src={content.imageUrl} alt="" style={{
              width: '100%',
              height: isMobile ? '200px' : '260px',
              objectFit: 'cover',
              borderRadius: '12px'
            }} />
          ) : (
            <div style={{
              width: '100%',
              height: isMobile ? '200px' : '260px',
              backgroundColor: '#e5e7eb',
              borderRadius: '12px'
            }} />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{
            fontSize: isMobile ? '24px' : '28px',
            fontWeight: '700',
            color: content.textColor || '#1a1a1a',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.2'
          }}>
            {content.headline || 'Feature Story'}
          </h2>
          <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.7', margin: 0 }}>
            {content.body || 'Add your compelling story here...'}
          </p>
        </div>
      </div>
    )
  }

  // Layout: Bento 2-Column (NEW)
  if (block.type === 'layout-bento-2col') {
    const imgStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '8px' }
    const placeholder = (h) => ({ width: '100%', height: h, backgroundColor: '#e5e7eb', borderRadius: '8px' })
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: isMobile ? '20px 24px' : '24px 32px'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
          gap: '6px',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Main large image */}
          <div style={{
            gridRow: isMobile ? 'auto' : '1 / 3',
            position: 'relative',
            minHeight: isMobile ? '200px' : '320px',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {content.mainImage ? (
              <img src={content.mainImage} alt="" style={{ ...imgStyle, position: 'absolute', inset: 0, height: '100%' }} />
            ) : (
              <div style={placeholder('100%')} />
            )}
            {content.mainText && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: isMobile ? '16px' : '24px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                color: 'white',
                fontSize: isMobile ? '18px' : '22px',
                fontWeight: '700',
                letterSpacing: '-0.01em'
              }}>
                {content.mainText}
              </div>
            )}
          </div>
          {/* Top right */}
          <div style={{ minHeight: isMobile ? '160px' : '157px', borderRadius: '8px', overflow: 'hidden' }}>
            {content.topImage ? (
              <img src={content.topImage} alt="" style={imgStyle} />
            ) : (
              <div style={placeholder(isMobile ? '160px' : '157px')} />
            )}
          </div>
          {/* Bottom right */}
          <div style={{ minHeight: isMobile ? '160px' : '157px', borderRadius: '8px', overflow: 'hidden' }}>
            {content.bottomImage ? (
              <img src={content.bottomImage} alt="" style={imgStyle} />
            ) : (
              <div style={placeholder(isMobile ? '160px' : '157px')} />
            )}
          </div>
        </div>
      </div>
    )
  }

  // Layout: Bento 3-Column (NEW)
  if (block.type === 'layout-bento-3col') {
    // Flat image1..image4 fields (written by the editor's per-cell pickers)
    // override the images array the palette ships with
    const baseImages = content.images || ['', '', '', '']
    const images = baseImages.map((img, i) => content[`image${i + 1}`] || img)
    const imgStyle = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
    const placeholderStyle = { width: '100%', height: '100%', backgroundColor: '#e5e7eb' }
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: isMobile ? '20px 24px' : '24px 32px'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
          gridTemplateRows: isMobile ? 'auto auto' : '160px 160px',
          gap: '6px',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Tall left - spans 2 rows on desktop */}
          <div style={{
            gridRow: isMobile ? 'auto' : '1 / 3',
            borderRadius: '8px',
            overflow: 'hidden',
            minHeight: isMobile ? '180px' : 'auto'
          }}>
            {images[0] ? <img src={images[0]} alt="" style={imgStyle} /> : <div style={placeholderStyle} />}
          </div>
          {/* Top right wide - spans 2 cols on desktop */}
          <div style={{
            gridColumn: isMobile ? 'auto' : '2 / 4',
            borderRadius: '8px',
            overflow: 'hidden',
            minHeight: isMobile ? '180px' : 'auto'
          }}>
            {images[1] ? <img src={images[1]} alt="" style={imgStyle} /> : <div style={placeholderStyle} />}
          </div>
          {/* Bottom left small */}
          <div style={{ borderRadius: '8px', overflow: 'hidden', minHeight: isMobile ? '180px' : 'auto' }}>
            {images[2] ? <img src={images[2]} alt="" style={imgStyle} /> : <div style={placeholderStyle} />}
          </div>
          {/* Bottom right small */}
          <div style={{ borderRadius: '8px', overflow: 'hidden', minHeight: isMobile ? '180px' : 'auto' }}>
            {images[3] ? <img src={images[3]} alt="" style={imgStyle} /> : <div style={placeholderStyle} />}
          </div>
        </div>
      </div>
    )
  }

  // Layout: Feature Card (NEW)
  if (block.type === 'layout-feature-card') {
    const radius = content.cornerRadius || 16
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '24px' : '32px 48px',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <div style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '480px',
          backgroundColor: content.backgroundColor || '#ffffff',
          borderRadius: `${radius}px`,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
        }}>
          <div style={{ height: isMobile ? '200px' : '240px', overflow: 'hidden' }}>
            {content.imageUrl ? (
              <img src={content.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', backgroundColor: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c4c4c4" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
            )}
          </div>
          <div style={{ padding: isMobile ? '24px 20px' : '32px' }}>
            <h3 style={{
              fontSize: isMobile ? '22px' : '26px',
              fontWeight: '700',
              color: content.textColor || '#1a1a1a',
              margin: '0 0 10px 0',
              letterSpacing: '-0.02em',
              lineHeight: '1.2'
            }}>
              {content.headline || "This Month's Special"}
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#6b7280',
              lineHeight: '1.6',
              margin: 0
            }}>
              {content.body || 'Treat yourself to something new.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Layout: Glass Card (NEW)
  if (block.type === 'layout-glass-card') {
    const gradients = {
      purple: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      teal: 'linear-gradient(135deg, #007EA0 0%, #059669 100%)',
      sunset: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      dark: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
      warm: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
    }
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          background: gradients[content.gradient] || gradients.purple,
          padding: isMobile ? '48px 24px' : '64px 48px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: isMobile ? '240px' : '300px'
        }}
      >
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.2)',
          padding: isMobile ? '32px 24px' : '48px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: isMobile ? '26px' : '32px',
            fontWeight: '700',
            color: content.textColor || 'white',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
            lineHeight: '1.2'
          }}>
            {content.headline || 'Exclusive Member Benefits'}
          </h2>
          <p style={{
            fontSize: isMobile ? '14px' : '16px',
            color: content.textColor || 'white',
            opacity: 0.85,
            margin: 0,
            lineHeight: '1.6'
          }}>
            {content.body || 'Unlock premium perks with every visit.'}
          </p>
        </div>
      </div>
    )
  }

  // Layout: Polaroid
  if (block.type === 'layout-polaroid') {
    const imgSize = isMobile ? '100%' : '320px'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '32px 24px' : '48px',
          textAlign: 'center',
          backgroundColor: content.backgroundColor || '#f9fafb'
        }}
      >
        <div style={{
          display: 'inline-block',
          padding: '16px 16px 28px',
          backgroundColor: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          transform: 'rotate(-2deg)',
          maxWidth: isMobile ? '85%' : 'auto'
        }}>
          {content.imageUrl ? (
            <img src={content.imageUrl} alt="" style={{
              width: imgSize,
              height: imgSize,
              maxWidth: '100%',
              objectFit: 'cover',
              display: 'block'
            }} />
          ) : (
            <div style={{
              width: imgSize,
              height: imgSize,
              maxWidth: '100%',
              aspectRatio: '1',
              backgroundColor: '#e5e7eb'
            }} />
          )}
          <p style={{
            fontSize: '16px',
            color: content.textColor || '#1a1a1a',
            marginTop: '18px',
            marginBottom: 0,
            fontFamily: content.fontFamily || 'Georgia, serif',
            fontStyle: 'italic'
          }}>
            {content.caption || 'Your caption here'}
          </p>
        </div>
      </div>
    )
  }

  // Layout: Rounded Image (NEW)
  if (block.type === 'layout-rounded-image') {
    const radius = content.cornerRadius || 24
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: isMobile ? '20px 24px' : '24px 32px'
        }}
      >
        {content.imageUrl ? (
          <img
            src={content.imageUrl}
            alt=""
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: `${radius}px`,
              maxHeight: '400px',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: isMobile ? '200px' : '280px',
            backgroundColor: '#e5e7eb',
            borderRadius: `${radius}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c4c4c4" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
        )}
      </div>
    )
  }

  // Layout: Badge (NEW)
  if (block.type === 'layout-badge') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '40px 24px' : '56px 48px',
          textAlign: 'center',
          backgroundColor: content.backgroundColor || '#ffffff'
        }}
      >
        <div style={{
          display: 'inline-block',
          padding: '6px 16px',
          backgroundColor: content.badgeColor || '#ef4444',
          borderRadius: '100px',
          marginBottom: '16px'
        }}>
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: '#ffffff',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            {content.badgeText || 'NEW'}
          </span>
        </div>
        <h2 style={{
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: '800',
          color: content.textColor || '#1a1a1a',
          margin: '0 0 10px 0',
          letterSpacing: '-0.02em',
          lineHeight: '1.15'
        }}>
          {content.headline || 'Just Launched'}
        </h2>
        {content.body && (
          <p style={{
            fontSize: '15px',
            color: '#6b7280',
            margin: 0,
            maxWidth: '400px',
            marginLeft: 'auto',
            marginRight: 'auto',
            lineHeight: '1.6'
          }}>
            {content.body}
          </p>
        )}
      </div>
    )
  }

  // Layout: Countdown — delegates to CountdownBlock component for live timer
  if (block.type === 'layout-countdown') {
    return <CountdownBlock content={content} isMobile={isMobile} wrapperStyle={wrapperStyle} handleClick={handleClick} />
  }

  // Layout: Stats
  if (block.type === 'layout-stats') {
    const stats = content.stats || [
      { value: '500+', label: 'Happy Clients' },
      { value: '15', label: 'Years' },
      { value: '4.9', label: 'Rating' }
    ]
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '40px 24px' : '48px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: isMobile ? '24px' : '56px',
          backgroundColor: content.backgroundColor || 'transparent'
        }}
      >
        {stats.map((stat, idx) => (
          <div key={idx} style={{
            textAlign: 'center',
            minWidth: isMobile ? '80px' : 'auto'
          }}>
            <p style={{
              fontSize: isMobile ? '36px' : '48px',
              fontWeight: '800',
              color: content.textColor || '#007EA0',
              margin: '0 0 4px 0',
              letterSpacing: '-0.03em',
              lineHeight: '1'
            }}>
              {stat.value}
            </p>
            <p style={{ fontSize: '13px', color: content.labelColor || '#6b7280', margin: 0, fontWeight: '500' }}>{stat.label}</p>
          </div>
        ))}
      </div>
    )
  }

  // Layout: Testimonial
  if (block.type === 'layout-testimonial') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '40px 24px' : '56px 48px',
          textAlign: 'center',
          backgroundColor: content.backgroundColor || '#f9fafb'
        }}
      >
        {/* Large decorative quote */}
        <div style={{
          fontSize: '64px',
          lineHeight: 1,
          color: '#e0e0e0',
          fontFamily: 'Georgia, serif',
          marginBottom: '4px',
          userSelect: 'none'
        }}>"</div>
        {content.avatarUrl && (
          <img
            src={content.avatarUrl}
            alt=""
            style={{
              width: isMobile ? '72px' : '88px',
              height: isMobile ? '72px' : '88px',
              borderRadius: '50%',
              objectFit: 'cover',
              margin: '0 auto 24px',
              display: 'block'
            }}
          />
        )}
        <p style={{
          fontSize: isMobile ? '17px' : '20px',
          color: content.textColor || '#374151',
          lineHeight: '1.8',
          fontStyle: 'italic',
          margin: '0 0 24px 0',
          maxWidth: '520px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          "{content.quote || 'Amazing experience!'}"
        </p>
        <p style={{
          fontSize: '16px',
          fontWeight: '700',
          color: content.textColor || '#1a1a1a',
          margin: '0 0 2px 0',
          letterSpacing: '-0.01em'
        }}>
          {content.name || 'Client Name'}
        </p>
        {content.title && (
          <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0, fontWeight: '500' }}>
            {content.title}
          </p>
        )}
      </div>
    )
  }

  // Layout: CTA Gradient
  if (block.type === 'layout-cta-gradient') {
    const btnOutline = content.buttonVariant === 'outline'
    const btnPill = content.buttonShape === 'pill'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          background: content.background || 'linear-gradient(135deg, #007EA0 0%, #059669 100%)',
          padding: isMobile ? '48px 24px' : '64px 48px',
          textAlign: 'center'
        }}
      >
        {content.eyebrow && (
          <p style={{
            fontSize: '11px',
            fontWeight: '600',
            color: content.eyebrowColor || content.headlineColor || 'white',
            opacity: content.eyebrowColor ? 1 : 0.7,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            margin: '0 0 16px 0',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {content.eyebrow}
          </p>
        )}
        <h3 style={{
          fontSize: isMobile ? '26px' : '34px',
          fontWeight: content.headlineWeight || '700',
          color: content.headlineColor || 'white',
          margin: content.subtext ? '0 0 12px 0' : '0 0 28px 0',
          letterSpacing: '-0.02em',
          lineHeight: '1.15'
        }}>
          {content.headline || 'Ready to Book?'}
        </h3>
        {content.subtext && (
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: content.headlineColor || 'white',
            opacity: 0.75,
            margin: '0 0 28px 0',
            lineHeight: '1.6',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {content.subtext}
          </p>
        )}
        <a
          href={content.buttonUrl || '#'}
          style={{
            display: 'inline-block',
            padding: btnPill ? (isMobile ? '13px 36px' : '14px 44px') : (isMobile ? '14px 32px' : '16px 40px'),
            backgroundColor: btnOutline ? 'transparent' : (content.buttonColor || 'white'),
            color: btnOutline ? (content.buttonColor || 'white') : (content.buttonTextColor || '#007EA0'),
            border: btnOutline ? `1.5px solid ${content.buttonColor || 'white'}` : 'none',
            borderRadius: btnPill ? '100px' : '10px',
            fontSize: content.buttonUppercase ? '12px' : '15px',
            fontWeight: content.buttonUppercase ? '600' : '700',
            textDecoration: 'none',
            letterSpacing: content.buttonUppercase ? '0.14em' : '-0.01em',
            textTransform: content.buttonUppercase ? 'uppercase' : 'none',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}
        >
          {content.buttonText || 'Book Now'}
        </a>
      </div>
    )
  }

  // Layout: Promo Banner
  if (block.type === 'layout-promo-banner') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          backgroundColor: content.backgroundColor || '#fbbf24',
          padding: isMobile ? '28px 24px' : '32px 48px',
          textAlign: 'center'
        }}
      >
        <p style={{
          fontSize: isMobile ? '24px' : '28px',
          fontWeight: '800',
          color: content.promoTextColor || '#1a1a1a',
          margin: '0 0 4px 0',
          letterSpacing: '0.02em'
        }}>
          {content.promoText || '20% OFF'}
        </p>
        {content.subtext && (
          <p style={{
            fontSize: isMobile ? '13px' : '15px',
            color: content.subtextColor || '#374151',
            margin: 0,
            fontWeight: '500'
          }}>
            {content.subtext}
          </p>
        )}
      </div>
    )
  }

  // Layout: Before/After
  if (block.type === 'layout-before-after') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '24px' : '24px 32px',
          backgroundColor: content.sectionColor || 'transparent'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '20px'
        }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            {content.beforeImage ? (
              <img src={content.beforeImage} alt="Before" style={{
                width: '100%',
                height: isMobile ? '200px' : '240px',
                objectFit: 'cover',
                borderRadius: '12px'
              }} />
            ) : (
              <div style={{
                width: '100%',
                height: isMobile ? '200px' : '240px',
                backgroundColor: '#fecaca',
                borderRadius: '12px'
              }} />
            )}
            <p style={{
              fontSize: '15px',
              fontWeight: '700',
              color: '#374151',
              marginTop: '12px',
              letterSpacing: '-0.01em'
            }}>
              {content.beforeLabel || 'Before'}
            </p>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            {content.afterImage ? (
              <img src={content.afterImage} alt="After" style={{
                width: '100%',
                height: isMobile ? '200px' : '240px',
                objectFit: 'cover',
                borderRadius: '12px'
              }} />
            ) : (
              <div style={{
                width: '100%',
                height: isMobile ? '200px' : '240px',
                backgroundColor: '#bbf7d0',
                borderRadius: '12px'
              }} />
            )}
            <p style={{
              fontSize: '15px',
              fontWeight: '700',
              color: '#374151',
              marginTop: '12px',
              letterSpacing: '-0.01em'
            }}>
              {content.afterLabel || 'After'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Layout: Price Card
  if (block.type === 'layout-price-card') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '20px 24px' : '24px 32px',
          backgroundColor: content.sectionColor || 'transparent'
        }}
      >
        <div style={{
          padding: isMobile ? '24px' : '32px',
          backgroundColor: content.cardBgColor || '#f9fafb',
          borderRadius: '12px',
          borderLeft: `4px solid ${content.accentColor || '#007EA0'}`
        }}>
          <h4 style={{
            fontSize: isMobile ? '18px' : '20px',
            fontWeight: '700',
            color: '#1a1a1a',
            margin: '0 0 8px 0',
            letterSpacing: '-0.01em'
          }}>
            {content.serviceName || 'Service Name'}
          </h4>
          {content.description && (
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0', lineHeight: '1.6' }}>
              {content.description}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {content.originalPrice && (
              <span style={{ fontSize: '16px', color: '#9ca3af', textDecoration: 'line-through' }}>
                £{content.originalPrice}
              </span>
            )}
            <span style={{
              fontSize: isMobile ? '28px' : '32px',
              fontWeight: '800',
              color: content.priceColor || '#059669',
              letterSpacing: '-0.02em'
            }}>
              £{content.salePrice || '99'}
            </span>
          </div>
        </div>
      </div>
    )
  }

  // Layout: Fancy Divider
  if (block.type === 'layout-fancy-divider') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: '24px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e5e5' }} />
        <div style={{ display: 'flex', gap: '6px' }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: content.color || '#007EA0' }} />
          ))}
        </div>
        <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e5e5' }} />
      </div>
    )
  }

  // Layout: Wave Divider
  if (block.type === 'layout-wave-divider') {
    const position = content.position || 'bottom'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          height: '60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            width: '100%',
            height: '60px',
            [position]: 0,
            transform: position === 'top' ? 'rotate(180deg)' : 'none'
          }}
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            fill={content.color || '#f3f4f6'}
          />
        </svg>
      </div>
    )
  }

  // Layout: Contact Card
  if (block.type === 'layout-contact-card') {
    const iconSize = isMobile ? '18' : '20'
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          fontFamily: content.fontFamily || 'Inter, system-ui, sans-serif',
          padding: isMobile ? '20px 24px' : '24px 32px'
        }}
      >
        <div style={{
          padding: isMobile ? '24px' : '32px',
          backgroundColor: content.backgroundColor || '#f9fafb',
          borderRadius: '12px'
        }}>
          {content.address && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' }}>
              <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span style={{ fontSize: '14px', color: content.textColor || '#374151', lineHeight: '1.5' }}>{content.address}</span>
            </div>
          )}
          {content.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
              <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <span style={{ fontSize: '14px', color: content.textColor || '#374151' }}>{content.phone}</span>
            </div>
          )}
          {content.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
              <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <span style={{ fontSize: '14px', color: content.textColor || '#374151' }}>{content.email}</span>
            </div>
          )}
          {content.hours && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span style={{ fontSize: '14px', color: content.textColor || '#374151' }}>{content.hours}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Layout: Social Strip
  if (block.type === 'layout-social-strip') {
    const socials = [
      { key: 'facebook', url: content.facebook },
      { key: 'instagram', url: content.instagram },
      { key: 'twitter', url: content.twitter },
      { key: 'tiktok', url: content.tiktok }
    ].filter(s => s.url)

    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: '28px 32px',
          textAlign: 'center'
        }}
      >
        <div style={{ display: 'flex', gap: isMobile ? '16px' : '14px', justifyContent: 'center' }}>
          {socials.length > 0 ? socials.map(social => (
            <a
              key={social.key}
              href={social.url}
              style={{
                width: '44px',
                height: '44px',
                backgroundColor: content.iconColor || '#29282D',
                borderRadius: content.style === 'circle' ? '50%' : '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none'
              }}
            >
              <SocialIcon platform={social.key} />
            </a>
          )) : (
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>Add social links in the editor</span>
          )}
        </div>
      </div>
    )
  }

  // Layout: Utility Bar — tiny letterspaced caps strip, left/right aligned
  if (block.type === 'layout-utility-bar') {
    const hasBoth = content.leftText && content.rightText
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          padding: isMobile ? '14px 20px' : '14px 32px',
          backgroundColor: content.backgroundColor || 'transparent',
          borderBottom: content.showRule ? `1px solid ${content.ruleColor || '#e8e4de'}` : 'none',
          display: 'flex',
          justifyContent: hasBoth ? 'space-between' : 'center',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        <span style={{
          fontSize: '10px',
          fontWeight: '600',
          color: content.textColor || '#6b7280',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          fontFamily: 'Inter, system-ui, sans-serif',
          whiteSpace: 'nowrap'
        }}>
          {content.leftText || 'YOUR SALON'}
        </span>
        {content.rightText && (
          <span style={{
            fontSize: '10px',
            fontWeight: '600',
            color: content.textColor || '#6b7280',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            fontFamily: 'Inter, system-ui, sans-serif',
            whiteSpace: 'nowrap'
          }}>
            {content.rightText}
          </span>
        )}
      </div>
    )
  }

  // Layout: Editorial Hero — typography-led hero on a solid background
  if (block.type === 'layout-editorial-hero') {
    const headlineLines = (content.headline || 'Headline').split('\n')
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          backgroundColor: content.backgroundColor || '#ffffff',
          padding: isMobile ? '48px 24px' : '72px 48px',
          textAlign: content.align || 'center'
        }}
      >
        {content.eyebrow && (
          <p style={{
            fontSize: '11px',
            fontWeight: '600',
            color: content.accentColor || content.textColor || '#1a1a1a',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            margin: '0 0 20px 0',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {content.eyebrow}
          </p>
        )}
        <h1 style={{
          fontSize: isMobile ? `${Math.round((content.headlineSize || 44) * 0.72)}px` : `${content.headlineSize || 44}px`,
          fontWeight: content.headlineWeight || '400',
          fontFamily: content.headlineFontFamily || 'Georgia, serif',
          fontStyle: content.headlineItalic ? 'italic' : 'normal',
          color: content.textColor || '#1a1a1a',
          margin: 0,
          letterSpacing: content.headlineUppercase ? '0.01em' : '-0.02em',
          textTransform: content.headlineUppercase ? 'uppercase' : 'none',
          lineHeight: '1.12'
        }}>
          {headlineLines.map((line, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h1>
        {content.subtext && (
          <p style={{
            fontSize: isMobile ? '14px' : '15px',
            color: content.textColor || '#1a1a1a',
            opacity: 0.65,
            margin: '18px 0 0 0',
            lineHeight: '1.7',
            maxWidth: '440px',
            marginLeft: content.align === 'left' ? 0 : 'auto',
            marginRight: content.align === 'left' ? 0 : 'auto',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {content.subtext}
          </p>
        )}
      </div>
    )
  }

  // Layout: Section Intro — eyebrow + heading + body lead-in for a content section
  if (block.type === 'layout-section-intro') {
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          backgroundColor: content.backgroundColor || 'transparent',
          padding: isMobile ? '40px 24px 16px' : '56px 48px 20px',
          textAlign: 'center'
        }}
      >
        {content.eyebrow && (
          <p style={{
            fontSize: '11px',
            fontWeight: '600',
            color: content.accentColor || '#9ca3af',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            margin: '0 0 14px 0',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {content.eyebrow}
          </p>
        )}
        <h2 style={{
          fontSize: isMobile ? '23px' : '27px',
          fontWeight: content.headingWeight || '400',
          fontFamily: content.headingFontFamily || 'Georgia, serif',
          fontStyle: content.headingItalic ? 'italic' : 'normal',
          color: content.textColor || '#1a1a1a',
          margin: 0,
          letterSpacing: '-0.01em',
          lineHeight: '1.25'
        }}>
          {content.heading || 'Section Heading'}
        </h2>
        {content.body && (
          <p style={{
            fontSize: '14.5px',
            color: content.textColor || '#1a1a1a',
            opacity: 0.65,
            margin: '14px auto 0',
            lineHeight: '1.7',
            maxWidth: '420px',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {content.body}
          </p>
        )}
      </div>
    )
  }

  // Layout: Duo Grid — two image cards with captions in a colored panel
  if (block.type === 'layout-duo-grid') {
    const renderCard = (image, title, caption) => (
      <div style={{ flex: 1, minWidth: 0 }}>
        {image ? (
          <img src={image} alt="" style={{
            width: '100%',
            height: isMobile ? '180px' : '210px',
            objectFit: 'cover',
            borderRadius: '10px',
            display: 'block'
          }} />
        ) : (
          <div style={{
            width: '100%',
            height: isMobile ? '180px' : '210px',
            backgroundColor: 'rgba(0,0,0,0.08)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
        )}
        {title && (
          <p style={{
            fontSize: '14px',
            fontWeight: '700',
            color: content.textColor || '#1a1a1a',
            margin: '14px 0 4px 0',
            letterSpacing: '-0.01em',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {title}
          </p>
        )}
        {caption && (
          <p style={{
            fontSize: '12.5px',
            color: content.textColor || '#1a1a1a',
            opacity: 0.7,
            margin: 0,
            lineHeight: '1.55',
            fontFamily: 'Inter, system-ui, sans-serif'
          }}>
            {caption}
          </p>
        )}
      </div>
    )
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          backgroundColor: content.backgroundColor || 'transparent',
          padding: isMobile ? '32px 24px' : '40px 48px'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '28px' : '20px'
        }}>
          {renderCard(content.leftImage, content.leftTitle, content.leftCaption)}
          {renderCard(content.rightImage, content.rightTitle, content.rightCaption)}
        </div>
      </div>
    )
  }

  // Layout: Shape Image — photo cropped into a decorative shape, optional frame
  if (block.type === 'layout-shape-image') {
    const shape = SHAPE_DEFS[content.shape] || SHAPE_DEFS.arch
    // Inset so an optional frame stroke is not clipped at the viewBox edge
    const inset = 'translate(3.5 3.5) scale(0.93)'
    const clipId = `shapeclip-${content.shape || 'arch'}-${block.id}`
    const widthPct = Math.min(100, Math.max(30, content.width || 78))
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          backgroundColor: content.backgroundColor || 'transparent',
          padding: isMobile ? '28px 24px' : '36px 48px',
          textAlign: 'center'
        }}
      >
        <svg
          viewBox={`0 0 100 ${shape.viewH}`}
          style={{ width: `${widthPct}%`, maxWidth: '420px', display: 'inline-block', verticalAlign: 'top' }}
        >
          <defs>
            <clipPath id={clipId}>
              <path d={shape.d} transform={inset} />
            </clipPath>
          </defs>
          {content.imageUrl ? (
            <image
              href={content.imageUrl}
              xlinkHref={content.imageUrl}
              x="0"
              y="0"
              width="100"
              height={shape.viewH}
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#${clipId})`}
            />
          ) : (
            <path d={shape.d} transform={inset} fill="rgba(0,0,0,0.08)" />
          )}
          {content.frameColor && (
            <path d={shape.d} transform={inset} fill="none" stroke={content.frameColor} strokeWidth="3.5" />
          )}
        </svg>
      </div>
    )
  }

  // Layout: Quote Chips — short client quotes in a 2x2 grid of rounded chips
  if (block.type === 'layout-quote-chips') {
    const quotes = [content.q1, content.q2, content.q3, content.q4].filter(Boolean)
    return (
      <div
        onClick={handleClick}
        style={{
          ...wrapperStyle,
          backgroundColor: content.backgroundColor || 'transparent',
          padding: isMobile ? '24px' : '28px 48px'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '12px'
        }}>
          {(quotes.length ? quotes : ['Add short quotes', 'In the editor panel']).map((q, idx) => (
            <div key={idx} style={{
              backgroundColor: content.chipColor || '#ffffff',
              borderRadius: '16px',
              padding: '20px 18px',
              textAlign: 'center',
              fontSize: '13.5px',
              fontWeight: '600',
              color: content.textColor || '#1a1a1a',
              lineHeight: '1.45',
              fontFamily: 'Inter, system-ui, sans-serif'
            }}>
              {q}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Default fallback
  return (
    <div
      onClick={handleClick}
      style={{
        ...wrapperStyle,
        padding: '20px 32px',
        backgroundColor: '#f9fafb',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '14px'
      }}
    >
      Unknown block type: {block.type}
    </div>
  )
}

// Helper component for column items
const ColumnItem = ({ item }) => {
  if (item.type === 'text') {
    return (
      <p style={{
        fontSize: `${item.fontSize || 14}px`,
        color: item.textColor || '#374151',
        fontFamily: item.fontFamily || 'Inter, system-ui, sans-serif',
        fontWeight: item.bold ? '700' : '400',
        textAlign: item.align || 'left',
        margin: '0 0 12px 0',
        lineHeight: '1.5'
      }}>
        {item.bullet && '• '}{item.text || ''}
      </p>
    )
  }

  if (item.type === 'image') {
    return item.url ? (
      <img src={item.url} alt="" style={{ width: '100%', height: 'auto', borderRadius: '8px', marginBottom: '12px' }} />
    ) : null
  }

  if (item.type === 'button') {
    return (
      <a
        href={item.url || '#'}
        style={{
          display: 'inline-block',
          padding: '10px 20px',
          backgroundColor: item.buttonColor || '#29282D',
          color: item.textColor || 'white',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '600',
          textDecoration: 'none',
          marginBottom: '12px'
        }}
      >
        {item.text || 'Button'}
      </a>
    )
  }

  return null
}

// Social icon helper
const SocialIcon = ({ platform }) => {
  const icons = {
    facebook: <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>,
    instagram: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>,
    twitter: <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>,
    tiktok: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
  }
  return icons[platform] || null
}
